import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { useToast, ToastContainer } from "../../components/common/Toast";
import { blogAdminService, BlogArticleFull } from "../../services/blogAdminService";

// Edit one article: fields + markdown body + image upload + live (server-
// rendered) preview. Slug is read-only once published. See
// .claude/tasks/blog-automation.md.
const BlogArticleEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toasts, removeToast, success, error } = useToast();

  const [article, setArticle] = useState<BlogArticleFull | null>(null);
  const [body, setBody] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const a = await blogAdminService.getArticle(id);
      setArticle(a);
      setBody(a.body_markdown || "");
    } catch {
      error("Failed to load article");
    } finally {
      setLoading(false);
    }
  }, [id, error]);

  useEffect(() => {
    void load();
  }, [load]);

  const refreshPreview = useCallback(async () => {
    try {
      setPreviewHtml(await blogAdminService.preview(body));
    } catch {
      /* preview is best-effort */
    }
  }, [body]);

  useEffect(() => {
    const t = setTimeout(() => void refreshPreview(), 500);
    return () => clearTimeout(t);
  }, [refreshPreview]);

  if (loading) return <p className="text-gray-400">Loading…</p>;
  if (!article) return <p className="text-gray-400">Article not found.</p>;

  const set = <K extends keyof BlogArticleFull>(k: K, v: BlogArticleFull[K]) =>
    setArticle({ ...article, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      const updated = await blogAdminService.updateArticle(article.id, {
        title: article.title,
        body_markdown: body,
        meta_description: article.meta_description ?? "",
        excerpt: article.excerpt ?? "",
        category: article.category,
        keywords: article.keywords ?? "",
        hero_image_url: article.hero_image_url,
        status: article.status,
      });
      setArticle(updated);
      setBody(updated.body_markdown || "");
      success("Saved");
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        "Failed to save";
      error(msg);
    } finally {
      setBusy(false);
    }
  };

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const url = await blogAdminService.uploadImage(article.id, file);
      setBody((b) => `${b}\n\n![${file.name}](${url}){.wide}\n`);
      success("Image uploaded and inserted");
    } catch {
      error("Image upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const slugLocked = article.status === "published";

  return (
    <div className="max-w-6xl mx-auto">
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <div className="flex items-center justify-between mb-4">
        <button onClick={() => navigate("/admin/blog")} className="text-sm text-gray-400 hover:text-white">
          ← Blog admin
        </button>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" id="img-up" />
          <label htmlFor="img-up" className="px-3 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-white text-sm font-semibold cursor-pointer">
            Upload image
          </label>
          <button
            onClick={save}
            disabled={busy}
            className="px-4 py-2 rounded-md bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: fields + markdown */}
        <div className="space-y-3">
          <input
            value={article.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="Title"
            className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-white text-lg font-semibold"
          />
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-gray-400">
              Slug {slugLocked && <span className="text-gray-600">(locked)</span>}
              <input
                value={article.slug}
                readOnly={slugLocked}
                onChange={(e) => set("slug", e.target.value)}
                className="mt-1 w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-1.5 text-white disabled:opacity-60"
              />
            </label>
            <label className="text-xs text-gray-400">
              Status
              <select
                value={article.status}
                onChange={(e) => set("status", e.target.value as BlogArticleFull["status"])}
                className="mt-1 w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-1.5 text-white"
              >
                <option value="published">published</option>
                <option value="draft">draft</option>
                <option value="archived">archived</option>
              </select>
            </label>
            <label className="text-xs text-gray-400">
              Category
              <select
                value={article.category}
                onChange={(e) => set("category", e.target.value)}
                className="mt-1 w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-1.5 text-white"
              >
                <option>Article</option>
                <option>Resource</option>
                <option>Update</option>
              </select>
            </label>
            <label className="text-xs text-gray-400">
              Hero image URL
              <input
                value={article.hero_image_url ?? ""}
                onChange={(e) => set("hero_image_url", e.target.value || null)}
                className="mt-1 w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-1.5 text-white"
              />
            </label>
          </div>
          <label className="block text-xs text-gray-400">
            Meta description
            <input
              value={article.meta_description ?? ""}
              onChange={(e) => set("meta_description", e.target.value)}
              maxLength={155}
              className="mt-1 w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-1.5 text-white"
            />
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={24}
            className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-white font-mono text-sm"
            placeholder="# Markdown body"
          />
        </div>

        {/* Right: live preview (server-rendered HTML) */}
        <div>
          <p className="text-xs text-gray-500 mb-2">Live preview (server-rendered, sanitized)</p>
          <div
            className="prose prose-invert max-w-none bg-gray-900 border border-gray-800 rounded-md p-4 overflow-auto"
            style={{ minHeight: "32rem" }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml) }}
          />
        </div>
      </div>
    </div>
  );
};

export default BlogArticleEditor;
